import {
  ChatManageRelationshipAction,
  ChatRelationshipEntityRef,
} from "@mapmycustomers/shared/types/chat/ChatContent";
import { RELATIONSHIP_ENTITY_TYPES } from "@mapmycustomers/shared/util/chat/manageRelationshipPayload";

// The payload-shape rules moved to the shared package so the wire guard could reach them; the
// registry still reads the primary-flag rule through here.

// What a conflict tells the card. The three cases are genuinely different to a reader: the platform
// said nothing, the link is now empty, or somebody else put a different record there. A single
// `ref | undefined` would collapse the first two, and "nothing is linked" is not the same news as
// "we could not tell you".
export type FreshRelatedEntity =
  | { kind: "cleared" }
  | { kind: "linked"; ref: ChatRelationshipEntityRef }
  | { kind: "unknown" };

// A conflict is the only outcome carrying the record's real current state, and it is the only place
// that state exists — the proposal's own copy was frozen when the agent proposed it. Shaped
// `{ currentLink: ref | null }` for this action type.
const parseFreshRelatedEntity = (
  freshValues: Record<string, unknown> | undefined
): FreshRelatedEntity => {
  if (!freshValues || !("currentLink" in freshValues)) {
    return { kind: "unknown" };
  }
  const currentLink = freshValues.currentLink;
  if (currentLink === null) {
    return { kind: "cleared" };
  }
  if (typeof currentLink !== "object" || currentLink === null) {
    return { kind: "unknown" };
  }
  const link = currentLink as Record<string, unknown>;
  const entityType = RELATIONSHIP_ENTITY_TYPES.find((candidate) => candidate === link.entityType);
  if (!entityType || typeof link.name !== "string") {
    return { kind: "unknown" };
  }
  if (typeof link.id !== "number") {
    return { kind: "unknown" };
  }
  return { kind: "linked", ref: { id: link.id, entityType, name: link.name } };
};

// The flag is three-state on the wire, so a draft that carries a boolean wins over the proposal —
// including `false`, which is the rep unticking a primary the agent proposed.
export const resolveEffectivePrimary = (
  action: ChatManageRelationshipAction,
  draft: Record<string, unknown> | undefined
): boolean => (typeof draft?.primary === "boolean" ? draft.primary : action.setPrimary === true);

export default parseFreshRelatedEntity;
